import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./professionlist.css";
import {
  Phone,
  MessageCircle,
  Mail,
  Search,
  MapPin,
  BriefcaseBusiness,
  BadgeCheck,
  ArrowLeft,
  UserRound
} from "lucide-react";

export default function ProfessionList() {
  const { type } = useParams();
  const navigate = useNavigate();

  const [showConsult, setShowConsult] = useState(false);
  const [consultUser, setConsultUser] = useState(null);

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* =====================================================
     FETCH PROFESSIONAL DATA
  ===================================================== */

  useEffect(() => {
    setLoading(true);
    setError("");

    fetch(`/api/profession/${type}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setUsers(data.users || []);
        } else {
          setError("Failed to load profiles");
        }

        setLoading(false);
      })
      .catch(() => {
        setError("Server error. Please try again.");
        setLoading(false);
      });
  }, [type]);


  /* =====================================================
     SEARCH
     NAME + PROFESSION + LOCATION + SKILLS
  ===================================================== */

  const filteredUsers = useMemo(() => {

    const query = search
      .trim()
      .toLowerCase();

    if (!query) {
      return users;
    }

    return users.filter((user) => {

      const name =
        user.name || "";

      const profession =
        user.profession || "";

      const skills =
        Array.isArray(user.skills)
          ? user.skills.join(" ")
          : user.skills || "";

      const city =
        user.location?.city || "";

      const district =
        user.location?.district || "";

      const state =
        user.location?.state || "";

      const pincode =
        user.location?.pincode || "";

      const searchableText = [
        name,
        profession,
        skills,
        city,
        district,
        state,
        pincode
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });

  }, [users, search]);


  /* =====================================================
     DISPLAY PROFESSION
  ===================================================== */

  const displayProfession =
    type
      ? type.charAt(0).toUpperCase() +
        type.slice(1)
      : "Professionals";


  /* =====================================================
     OPEN CONTACT
  ===================================================== */

  const openContactProfessional = (professional) => {

    if (!localStorage.getItem("token")) {
      navigate("/");
      return;
    }

    setConsultUser(professional);
    setShowConsult(true);
  };


  /* =====================================================
     VIEW PROFILE
  ===================================================== */

  const viewProfile = (professional) => {

    if (!localStorage.getItem("token")) {
      navigate("/login");
      return;
    }

    navigate(
      `/profile/${professional._id}`
    );
  };


  /* =====================================================
     LOCATION
  ===================================================== */

  const getLocation = (user) => {

    const location =
      user.location;

    if (!location) {
      return null;
    }

    return [
      location.city,
      location.district
    ]
      .filter(Boolean)
      .join(", ");
  };


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {

    return (
      <div className="profession-page">

        <div className="profession-loading">

          <div className="loading-spinner" />

          <p>
            Finding {displayProfession.toLowerCase()}s...
          </p>

        </div>

      </div>
    );
  }


  /* =====================================================
     ERROR
  ===================================================== */

  if (error) {

    return (
      <div className="profession-page">

        <div className="profession-status-card">

          <p className="status-text error">
            {error}
          </p>

          <button
            className="retry-btn"
            onClick={() =>
              window.location.reload()
            }
          >
            Try Again
          </button>

        </div>

      </div>
    );
  }


  /* =====================================================
     MAIN
  ===================================================== */

  return (
    <div className="profession-page">

      {/* =================================================
          HEADER
      ================================================= */}

      <header className="profession-header">

        <button
          className="profession-back-btn"
          onClick={() =>
            navigate("/profession-dashboard")
          }
          aria-label="Back"
        >
          <ArrowLeft size={19} />
        </button>

        <div className="profession-header-content">

          <span className="profession-eyebrow">
            CIVILINK PROFESSIONALS
          </span>

          <h1 className="profession-title">
            {displayProfession}s
          </h1>

          <p className="profession-subtitle">
            Find professionals for your construction needs.
          </p>

        </div>

      </header>


      {/* =================================================
          SEARCH
      ================================================= */}

      <div className="profession-search">

        <Search
          size={18}
          className="profession-search-icon"
        />

        <input
          type="text"
          placeholder="Search name, profession, skill or location..."
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
        />

        {search && (

          <button
            className="profession-search-clear"
            onClick={() => setSearch("")}
            aria-label="Clear search"
          >
            ×
          </button>

        )}

      </div>


      {/* =================================================
          RESULT SUMMARY
      ================================================= */}

      <div className="profession-results-header">

        <div>

          <strong>
            {filteredUsers.length}
          </strong>

          <span>
            {filteredUsers.length === 1
              ? " professional"
              : " professionals"}
          </span>

        </div>

        {search && (
          <span className="search-result-label">
            Results for "{search}"
          </span>
        )}

      </div>


      {/* =================================================
          EMPTY SEARCH
      ================================================= */}

      {!filteredUsers.length && (

        <div className="profession-empty">

          <div className="profession-empty-icon">
            <Search size={23} />
          </div>

          <h3>
            No professionals found
          </h3>

          <p>
            Try another name, profession,
            skill or location.
          </p>

          {search && (

            <button
              onClick={() => setSearch("")}
              className="clear-search-btn"
            >
              Clear Search
            </button>

          )}

        </div>
      )}


      {/* =================================================
          PROFESSIONAL CARDS
      ================================================= */}

      <div className="profession-grid">

        {filteredUsers.map((u) => {

          const location =
            getLocation(u);

          return (

            <article
              key={u._id}
              className="profession-card"
              onClick={() =>
                viewProfile(u)
              }
            >

              {/* CARD HEADER */}

              <div className="profession-card-header">

                {u.profilePhoto ? (

                  <img
                    src={u.profilePhoto}
                    alt={u.name}
                    className="profession-avatar-image"
                  />

                ) : (

                  <div className="profession-avatar">

                    {u.name
                      ?.charAt(0)
                      ?.toUpperCase() || (
                        <UserRound size={20} />
                      )}

                  </div>

                )}

                <div className="profession-basic">

                  <div className="profession-name-row">

                    <div className="profession-name">
                      {u.name || "Professional"}
                    </div>

                    <BadgeCheck
                      size={16}
                      className="verified-icon"
                    />

                  </div>

                  <div className="profession-role">
                    {u.profession || displayProfession}
                  </div>

                </div>

              </div>


              {/* PROFESSIONAL DETAILS */}

              <div className="profession-details">

                {location && (

                  <div className="profession-detail">

                    <MapPin size={15} />

                    <span>
                      {location}
                    </span>

                  </div>

                )}

                <div className="profession-detail">

                  <BriefcaseBusiness size={15} />

                  <span>
                    {u.experienceYears || 0} years experience
                  </span>

                </div>

              </div>


              {/* SKILLS */}

              {u.skills?.length > 0 && (

                <div className="profession-skills">

                  {u.skills
                    .slice(0, 5)
                    .map((skill, i) => (

                      <span
                        key={i}
                        className="skill-chip"
                      >
                        {skill}
                      </span>

                    ))}

                  {u.skills.length > 5 && (

                    <span className="skill-more">
                      +{u.skills.length - 5}
                    </span>

                  )}

                </div>

              )}


              {/* DESCRIPTION */}

              {u.bio && (

                <p className="profession-description">

                  {u.bio.length > 100
                    ? `${u.bio.slice(0, 100)}...`
                    : u.bio}

                </p>

              )}


              {/* FOOTER */}

              <div
                className="profession-footer"
                onClick={(e) =>
                  e.stopPropagation()
                }
              >

                <button
                  className="view-profile-btn"
                  onClick={() =>
                    viewProfile(u)
                  }
                >
                  View Profile
                </button>

                <button
                  className="consult-btn"
                  onClick={() =>
                    openContactProfessional(u)
                  }
                >
                  Consult
                </button>

              </div>

            </article>

          );

        })}

      </div>


      {/* =================================================
          CONTACT SHEET
      ================================================= */}

      {showConsult && (

        <div
          className="customer-sheet-overlay"
          onClick={() =>
            setShowConsult(false)
          }
        >

          <div
            className="customer-sheet"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="sheet-handle" />

            <h3 className="sheet-title">
              Contact Professional
            </h3>

            <p className="sheet-subtitle">
              Choose how you'd like to connect.
            </p>


            {consultUser?.phone && (

              <>
                <button
                  className="sheet-btn primary"
                  onClick={() => {

                    window.location.href =
                      `tel:${consultUser.phone}`;

                    setShowConsult(false);

                  }}
                >
                  <Phone size={18} />
                  <span>Call</span>
                </button>


                <button
                  className="sheet-btn primary"
                  onClick={() => {

                    const text =
                      encodeURIComponent(
                        `Hello ${consultUser.name},

I found your profile on Civilink and would like to discuss my construction requirements.`
                      );

                    window.open(
                      `https://wa.me/91${consultUser.phone}?text=${text}`,
                      "_blank"
                    );

                    setShowConsult(false);

                  }}
                >
                  <MessageCircle size={18} />
                  <span>WhatsApp</span>
                </button>
              </>

            )}


            {consultUser?.email && (

              <button
                className="sheet-btn primary"
                onClick={() => {

                  window.location.href =
                    `mailto:${consultUser.email}?subject=Construction Inquiry from Civilink`;

                  setShowConsult(false);

                }}
              >
                <Mail size={18} />
                <span>Email</span>
              </button>

            )}


            {!consultUser?.phone &&
              !consultUser?.email && (

                <div className="contact-unavailable">
                  Contact information not available.
                </div>

              )}


            <button
              className="sheet-btn outline"
              onClick={() =>
                setShowConsult(false)
              }
            >
              Close
            </button>

          </div>

        </div>

      )}

    </div>
  );
}